import { FC, useState, useEffect } from 'react';
import { Calendar, dateFnsLocalizer } from 'react-big-calendar';
import { FaChevronLeft, FaChevronRight } from 'react-icons/fa';

import { buildClientEntity } from 'utils/helpers/client';
import { useActiveClientContext } from 'store/active-client';
import { getSubdomain } from 'utils/helpers/routing';

import { useQuery } from 'react-query';
import API from 'core/Api';
import { useLanguageContext } from 'store/language';
import { useTranslation } from 'react-i18next';
import format from 'date-fns/format';
import parse from 'date-fns/parse';
import startOfWeek from 'date-fns/startOfWeek';
import getDay from 'date-fns/getDay';
import enGB from 'date-fns/locale/en-GB';
import bg from 'date-fns/locale/bg';
import styled from 'styled-components';
import CustomEventWrapper from '../components/CustomEventWrapper';
import { ILeave } from '../types';

import 'react-big-calendar/lib/css/react-big-calendar.css';

interface CalendarProps {
  onAddOvertime: (date: Date) => void;
  onDateChange: (date: Date) => void;
}

const Screen: FC<CalendarProps> = ({ onAddOvertime, onDateChange }) => {
  const { t } = useTranslation();
  const { client } = useActiveClientContext();
  const { language } = useLanguageContext();
  const isOperator = getSubdomain() === 'operators';

  const messages = {
    allDay: t('leaves.employer.calendar.allDay'),
    previous: t('leaves.employer.calendar.previous'),
    next: t('leaves.employer.calendar.next'),
    today: t('leaves.employer.calendar.today'),
    month: t('leaves.employer.calendar.month'),
    week: t('leaves.employer.calendar.week'),
    day: t('leaves.employer.calendar.day'),
    agenda: t('leaves.employer.calendar.agenda'),
    date: t('leaves.employer.calendar.date'),
    time: t('leaves.employer.calendar.time'),
    event: t('leaves.employer.calendar.event'),
    showMore: (total: number) => t('leaves.employer.calendar.showMore', { total }),
  };
  const locales = {
    bg,
    en: enGB,
  };

  const localizer = dateFnsLocalizer({
    format,
    parse,
    startOfWeek,
    getDay,
    locales,
  });

  const [date, setDate] = useState<Date>(new Date());
  const year = date.getFullYear();
  const [calendarDimensions, setCalendarDimensions] = useState(() => {
    if (window.innerWidth < 1600) return '500px';
    return '700px';
  });

  // This will update the state, when month is changed through the Calendar component.
  const [calendarMonth, setCalendarMonth] = useState<number>(date.getMonth());

  useEffect(() => {
    const handleResize = () => {
      setCalendarDimensions(window.innerWidth < 1600 ? '500px' : '700px');
    };

    window.addEventListener('resize', handleResize);

    // Cleanup event listener on component unmount
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  const {
    isLoading: isLoadingHolidays,
    error: errorHolidays,
    data: holidaysData = [],
  } = useQuery<ILeave[], Error>([buildClientEntity('holidays', client), year], (): Promise<ILeave[]> => {
    return API.get(`holidays/${year}`).then(res => {
      return res.data?.items?.map((holiday: any, index: number) => {
        // Set the start time to 00:00:00
        const start = new Date(holiday.date);
        start.setHours(0, 0, 0, 0);

        // Set the end time to 23:59:59
        const end = new Date(holiday.date);
        end.setHours(23, 59, 59, 999);

        return {
          ...holiday,
          id: index,
          title: holiday.name,
          start,
          end,
          type: 'holiday',
          allDay: true,
        };
      });
    });
  });

  const CustomToolbar = (toolbar: any) => {
    const goToBack = () => {
      toolbar.date.setMonth(toolbar.date.getMonth() - 1);
      toolbar.onNavigate('prev');
      setCalendarMonth(toolbar.date.getMonth());
    };

    const goToNext = () => {
      toolbar.date.setMonth(toolbar.date.getMonth() + 1);
      toolbar.onNavigate('next');
      setCalendarMonth(toolbar.date.getMonth());
    };
    return (
      <StyledCustomToolbar>
        <button
          type="button"
          onClick={goToBack}
        >
          <FaChevronLeft size={16} />
        </button>

        <h5 className="text-center">
          {t(`leaves.months.${date.getMonth() + 1}`)} {date.getFullYear()}
        </h5>

        <button
          type="button"
          onClick={goToNext}
        >
          <FaChevronRight size={16} />
        </button>
      </StyledCustomToolbar>
    );
  };

  const CustomEventWrapperWithProps = (props: any) => {
    return (
      <CustomEventWrapper
        {...props}
        overtimeCalendar
        onAddOvertime={onAddOvertime}
      />
    );
  };

  function getDaysInMonth(month: number, yearNow: number): Date[] {
    const startDate = new Date(yearNow, month, 1);
    startDate.setDate(startDate.getDate() - 7); // Subtract 7 days for the start

    const endDate = new Date(yearNow, month + 1, 0);
    endDate.setDate(endDate.getDate() + 7); // Add 7 days for the end

    const days: Date[] = [];

    while (startDate <= endDate) {
      days.push(new Date(startDate));
      startDate.setDate(startDate.getDate() + 1);
    }

    return days;
  }

  const daysInCurrentMonth = getDaysInMonth(calendarMonth, year);

  const allDaysEvents: any[] = daysInCurrentMonth
    .filter(day => {
      const formattedDate = format(day, 'yyyy-MM-dd');
      return !holidaysData.some(holiday => holiday.date === formattedDate);
    })
    .map((day, index) => {
      // Set the start time to 00:00:00
      const start = new Date(day);
      start.setHours(0, 0, 0, 0);

      // Set the end time to 23:59:59
      const end = new Date(day);
      end.setHours(23, 59, 59, 999);

      return {
        id: index,
        title: '',
        start,
        end,
        type: 'day',
        allDay: true,
        name: '',
        name_en: null,
        date: format(day, 'yyyy-MM-dd'),
        year: day.getFullYear(),
      };
    });

  const allCalendarEvents: ILeave[] = isOperator ? [] : [...allDaysEvents, ...holidaysData];

  return (
    <StyledCalendar>
      <Calendar
        date={date}
        events={allCalendarEvents as any}
        localizer={localizer}
        culture={language || 'bg'}
        views={{
          // Show only month view
          month: true,
        }}
        components={{
          // Override the event wrapper
          eventWrapper: CustomEventWrapperWithProps,
          toolbar: CustomToolbar,
        }}
        toolbar
        messages={messages}
        popup
        style={{ height: calendarDimensions, width: calendarDimensions }}
        onNavigate={currDay => {
          onDateChange(currDay);
        }}
        // @ts-ignore
        dayPropGetter={(d: Date) => {
          const month = d.getMonth() + 1; // getMonth() returns 0-based month, so add 1
          const day = d.getDate();
          const formattedDate = `${year}-${month >= 10 ? month : `0${month}`}-${day >= 10 ? day : `0${day}`}`;
          const holidaysDatesArr: string[] = [];
          // eslint-disable-next-line no-plusplus
          holidaysData.forEach(j => holidaysDatesArr.push(j.date));

          const dayOfWeek = getDay(d);
          const isWeekend = dayOfWeek === 6 || dayOfWeek === 0;

          if (holidaysDatesArr.includes(formattedDate) || isWeekend) {
            return {
              style: {
                backgroundColor: 'rgba(255, 229, 171, 0.6)',
              },
            };
          }
        }}
      />
    </StyledCalendar>
  );
};

export default Screen;

const StyledCalendar = styled.div`
  width: 100%;
  .rbc-calendar {
    margin: 0 auto;
    @media ${props => props.theme.breakpoints?.sm} {
      width: 100% !important;
    }

    .rbc-month-view {
      min-width: 10rem !important;
      overflow: auto;
      max-height: 600px;

      @media ${props => props.theme.breakpoints?.sm} {
        min-width: 500px !important;
      }
    }
  }
`;

const StyledCustomToolbar = styled.div`
  position: relative;
  display: flex;
  justify-content: center;
  align-items: center;
  margin-bottom: 16px;

  h5 {
    margin: 0;
    min-width: 360px;
  }

  button {
    border: 0;
    background: none;
    opacity: 0.4;
    transition: 0.3s;

    svg {
      fill: #575962;
    }

    &:hover {
      opacity: 1;
      transition: 0.3s;
    }
  }
`;
